import { useState } from 'react';
import type { AgentRunSummary } from '@erp/rpc';
import { useListAgentRuns } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import {
  historyLimitFor,
  runHistoryFilterKey,
  toListRunsFilter,
  type RunHistoryFilter,
} from './agentRunHistoryFilter';
import { useRunHistoryFilter } from './useRunHistoryFilter';

/** History page size; "Show older runs" grows the window by this much. */
const RUNS_PAGE_SIZE = 20;

/** Stats, header and live-run detection always look past skipped checks. */
const RECENT_RUNS_FILTER = toListRunsFilter({ hideSkipped: true });

export interface AgentPageRuns {
  /** Newest runs that did (or are doing) work: the stat strip and header. */
  recentRuns: AgentRunSummary[];
  /** When the agent last tried to run, skipped checks included. */
  latestAttemptAt: string | null;
  history: {
    runs: AgentRunSummary[];
    total: number;
    /** Runs the History filter left out; null when the server did not say. */
    hiddenCount: number | null;
    /** True until History has answered once. A linked run waits on this. */
    isPending: boolean;
    isFetching: boolean;
    onLoadMore: () => void;
    filter: RunHistoryFilter;
    onFilterChange: (next: RunHistoryFilter) => void;
  };
}

/**
 * The two run lists an agent page reads. The stat strip describes how the
 * agent is doing now, so it always leaves skipped checks out, whatever History
 * shows; History follows the URL filter. With the default filter on the first
 * page both ask the same question, and react-query sends one request.
 */
export function useAgentPageRuns(agentDefinitionId: string): AgentPageRuns {
  const [filter, setFilter] = useRunHistoryFilter();
  // The window belongs to the filter it was grown under, so any filter change
  // — the switch, a link, Back — starts the new list from its first page.
  const filterKey = runHistoryFilterKey(filter);
  const [paging, setPaging] = useState({
    filterKey,
    limit: RUNS_PAGE_SIZE,
  });
  // Drop the old window as soon as the filter moves (React's "adjust state
  // while rendering"), so switching back later starts from the first page
  // too instead of restoring the window grown under that filter.
  if (paging.filterKey !== filterKey) {
    setPaging({ filterKey, limit: RUNS_PAGE_SIZE });
  }
  const runsLimit = historyLimitFor(paging, filterKey, RUNS_PAGE_SIZE);

  const { data: recentData } = useListAgentRuns(agentDefinitionId, {
    limit: RUNS_PAGE_SIZE,
    filter: RECENT_RUNS_FILTER,
  });
  const {
    data: historyData,
    isFetching,
    isPending,
  } = useListAgentRuns(
    agentDefinitionId,
    { limit: runsLimit, filter: toListRunsFilter(filter) },
    { keepPreviousResult: true },
  );

  const historyRuns = historyData?.runs ?? [];
  return {
    recentRuns: recentData?.runs ?? [],
    latestAttemptAt: recentData?.latestAttemptAt ?? null,
    history: {
      runs: historyRuns,
      total: historyData?.total ?? historyRuns.length,
      hiddenCount: historyData?.hiddenCount ?? null,
      isPending,
      isFetching,
      onLoadMore: () =>
        setPaging({ filterKey, limit: runsLimit + RUNS_PAGE_SIZE }),
      filter,
      onFilterChange: setFilter,
    },
  };
}
